/**
 * PostHog CTA tracking convention. See docs/analytics/cta-tracking.md.
 *
 * `cta_intent` is the locale-independent source of truth for conversion
 * actions. `cta_label` stays as the visible text (historical data), and
 * `cta_location` records where on the page the CTA sits. Never track CTAs by
 * element ID.
 */

export const CTA_INTENTS = ["apply", "book_call", "sign_in"] as const;
export type CtaIntent = (typeof CTA_INTENTS)[number];

export const CTA_LOCATIONS = [
  "nav",
  "hero",
  "intro",
  "body",
  "footer",
  "announcement",
] as const;
export type CtaLocation = (typeof CTA_LOCATIONS)[number];

const APP_HOSTNAME = "app.wayflyer.com";
const SITE_HOSTNAMES = ["wayflyer.com", "www.wayflyer.com"];

/** App paths that start an application or a funding offer. */
const APPLY_PATH_PREFIXES = [
  "/authn/signup",
  "/funding/how-to-apply",
  "/funding-offer",
];
const SIGN_IN_PATH_PREFIXES = ["/authn/login"];
const BOOK_CALL_PATHS = ["/book-a-call"];

function matchesPrefix(pathname: string, prefixes: readonly string[]) {
  return prefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function toUrlParts(href: string): { hostname: string; pathname: string } {
  try {
    const url = new URL(href, "https://wayflyer.com");
    return { hostname: url.hostname, pathname: url.pathname };
  } catch {
    return { hostname: "", pathname: "" };
  }
}

/**
 * Derives the CTA intent from a link destination so every component that
 * renders a link to the application flow is tagged without per-page wiring.
 * Returns undefined for links that are not a tracked CTA destination.
 */
export function getCtaIntent(href: unknown): CtaIntent | undefined {
  if (typeof href !== "string" || href === "") return undefined;
  const { hostname, pathname } = toUrlParts(href);
  const normalised = pathname.replace(/\/+$/, "") || "/";

  if (hostname === APP_HOSTNAME) {
    if (matchesPrefix(normalised, APPLY_PATH_PREFIXES)) return "apply";
    if (matchesPrefix(normalised, SIGN_IN_PATH_PREFIXES)) return "sign_in";
    return undefined;
  }

  // Locale-prefixed internal routes, e.g. /de/book-a-call.
  const withoutLocale = normalised.replace(
    /^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i,
    ""
  );
  if (
    SITE_HOSTNAMES.includes(hostname) &&
    BOOK_CALL_PATHS.includes(withoutLocale)
  ) {
    return "book_call";
  }

  return undefined;
}

interface CtaTrackingInput {
  intent?: CtaIntent;
  location?: CtaLocation;
  label?: string;
}

/** `data-ph-capture-attribute-*` props for a tracked CTA. */
export function getCtaTrackingProps({
  intent,
  location,
  label,
}: CtaTrackingInput) {
  return {
    "data-ph-capture-attribute-cta_label": label || undefined,
    "data-ph-capture-attribute-cta_intent": intent,
    "data-ph-capture-attribute-cta_location": intent ? location : undefined,
  };
}
